import React, { useState, useRef, useEffect } from 'react';
import { PROVINCE, trovaSigla } from '../../data/province';
import { useSpeechInput } from '../../hooks/useSpeechInput';

interface ProvinciaInputProps {
  value: string;
  onChange: (sigla: string) => void;
  placeholder?: string;
}

const ProvinciaInput: React.FC<ProvinciaInputProps> = ({ value, onChange, placeholder = 'Provincia...' }) => {
  const [testo, setTesto] = useState(value);
  const [aperto, setAperto] = useState(false);
  const [suggerimenti, setSuggerimenti] = useState<typeof PROVINCE>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTesto(value);
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        conferma();
        setAperto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  });

  const { isListening, isSupported, isModelReady, startListening, stopListening } = useSpeechInput({
    onResult: (text) => {
      const sigla = trovaSigla(text);
      setTesto(sigla);
      onChange(sigla);
      setAperto(false);
    },
    onError: () => {},
  });

  const filtra = (input: string) => {
    const q = input.toLowerCase();
    return PROVINCE.filter(p =>
      p.nome.toLowerCase().includes(q) ||
      p.sigla.toLowerCase().includes(q) ||
      p.regione.toLowerCase().includes(q)
    ).slice(0, 8);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setTesto(val);
    if (val.length >= 2) {
      const risultati = filtra(val);
      setSuggerimenti(risultati);
      setAperto(risultati.length > 0);
    } else {
      setAperto(false);
    }
  };

  const seleziona = (sigla: string) => {
    setTesto(sigla);
    onChange(sigla);
    setAperto(false);
  };

  const conferma = () => {
    const sigla = trovaSigla(testo);
    if (sigla) {
      setTesto(sigla);
      onChange(sigla);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={testo}
        onChange={handleChange}
        onBlur={conferma}
        placeholder={placeholder}
        maxLength={30}
        style={{
          width: '100%',
          paddingRight: isSupported ? 36 : 10,
          padding: '8px 10px',
          background: '#0a1628',
          border: '1px solid #1e3a5f',
          borderRadius: 6,
          color: '#e2e8f0',
          fontSize: 14,
          outline: isListening ? '1px solid #dc2626' : 'none',
          boxSizing: 'border-box',
        }}
      />

      {isSupported && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={isListening ? stopListening : startListening}
          title={isListening ? 'Ferma dettatura' : isModelReady ? 'Ditta nome provincia' : 'Modello in caricamento…'}
          style={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            background: isListening ? 'rgba(220,38,38,0.15)' : 'transparent',
            border: 'none',
            color: isListening ? '#ef4444' : '#64748b',
            cursor: 'pointer',
            padding: '2px 4px',
            borderRadius: 4,
            fontSize: 15,
            lineHeight: 1,
          }}
        >
          {isListening ? '⏹' : isModelReady ? '🎙' : '⏳'}
        </button>
      )}

      {aperto && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#0d1520',
          border: '1px solid #1e3a5f',
          borderRadius: 6,
          zIndex: 1000,
          maxHeight: 220,
          overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        }}>
          {suggerimenti.map(p => (
            <div
              key={p.sigla}
              onMouseDown={() => seleziona(p.sigla)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                borderBottom: '1px solid #1e3a5f',
              }}
            >
              <span style={{ fontWeight: 700, color: '#38bdf8', fontSize: 14, minWidth: 28 }}>{p.sigla}</span>
              <span style={{ color: '#e2e8f0', fontSize: 13 }}>{p.nome}</span>
              <span style={{ color: '#475569', fontSize: 11, marginLeft: 'auto' }}>{p.regione}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProvinciaInput;
